import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Badge, Segmented, type BadgeStatus } from '@indipia/ui-core';
import { apiClient } from '@indipia/shared/api/client';
import { COMPS, CROSS_ITEMS, type LocalComp, type Verdict } from './data';
import { SCREENS } from './screens';
import { demos as billingDemos } from './demos/billing';
import { demos as photoDemos } from './demos/photo-organizer';
import { demos as portfolioDemos } from './demos/portfolio';
import { demos as schedulerDemos } from './demos/scheduler';
import { demos as worklogDemos } from './demos/work-log';

// 샘플의 카톡 발송·등록 버튼이 로그인 토큰을 달고 실 서버로 나가지 않게 막는다.
//   /guides/agent-components  — 셸이 없는 공개 화면이라 요청을 전부 막는다(로그인 없이 열람).
//   /agent-components         — 앱 레이아웃(LNB·TopBar) 안이라 셸의 조회는 살려야 한다 → 쓰기(GET 외)만 막는다.
// 자식 컴포넌트의 마운트 fetch 가 부모 effect 보다 먼저 돌아서 생명주기 대신 모듈 로드 시 1회 등록하고 경로로 판정한다.
apiClient.interceptors.request.use((config) => {
  const path = window.location.pathname;
  const isWrite = (config.method ?? 'get').toLowerCase() !== 'get';
  if (path.startsWith('/guides/') || (path === '/agent-components' && isWrite)) {
    return Promise.reject(new Error(`[agent-gallery] API 차단: ${config.method ?? ''} ${config.url ?? ''}`));
  }
  return config;
});

const DEMOS: Record<string, Record<string, () => ReactNode>> = {
  billing: billingDemos,
  'photo-organizer': photoDemos,
  portfolio: portfolioDemos,
  scheduler: schedulerDemos,
  'work-log': worklogDemos,
};

const VERDICT_STATUS: Record<Verdict, BadgeStatus> = { 승격: 'info', 수정: 'warning', 유지: 'mute' };
// 판정은 검토 '의견'이다 — 완료 표시가 아니다. 배지에도 "필요"를 붙여 오해를 막는다.
const VERDICT_LABEL: Record<Verdict, string> = { 승격: '승격 필요', 수정: '수정 필요', 유지: '유지' };
const VERDICT_HELP: Record<Verdict, string> = {
  승격: '여러 에이전트에서 쓸 만한 범용 컴포넌트다. ui-core(또는 shared)로 옮기는 작업이 아직 남아 있다.',
  수정: '이미 있는 ui-core 컴포넌트로 바꾸거나, 하드코딩·접근성 같은 표준 위반을 고치는 작업이 아직 남아 있다.',
  유지: '그 에이전트 업무에만 쓰는 컴포넌트다. 지금 위치에 두면 되고 따로 할 작업은 없다.',
};
const AGENTS = [...new Set(COMPS.map((c) => c.agent))];
const count = (list: LocalComp[], v: Verdict) => list.filter((c) => c.verdict === v).length;
const cardId = (c: LocalComp) => `aglc-${c.agent}-${c.name}`.replace(/[^\w-]/g, '');

function readHash(): string {
  const h = decodeURIComponent(window.location.hash.slice(1));
  return AGENTS.includes(h) ? h : AGENTS[0];
}

/** 판정 도움말 — 필터 옆 ? 버튼으로 여는 레이어. 판정은 남은 작업(검토 의견)이지 완료 표시가 아니다 */
function VerdictHelp() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 바깥 클릭·Esc 로 닫기
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="판정 도움말"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-surface-raised text-sm font-semibold text-text-secondary"
      >
        ?
      </button>
      {open && (
        <div role="dialog" aria-label="판정 도움말" className="absolute left-0 top-full z-dropdown mt-2 w-80 sm:w-96 rounded-xxl border border-border bg-surface-raised p-4 shadow-emphasis">
          <p className="mb-3 text-sm font-semibold text-text">
            판정은 2026-10-16 검토 의견입니다. 작업이 끝났다는 표시가 아니라 앞으로 해야 할 일을 뜻합니다(아직 진행된 작업 없음).
          </p>
          <dl className="flex flex-col gap-3">
            {(['승격', '수정', '유지'] as const).map((v) => (
              <div key={v} className="flex flex-col gap-1">
                <dt>
                  <Badge status={VERDICT_STATUS[v]}>{VERDICT_LABEL[v]}</Badge>
                  <span className="ml-2 text-caption text-text-secondary">{count(COMPS, v)}개</span>
                </dt>
                <dd className="text-caption text-text-secondary">{VERDICT_HELP[v]}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-caption text-text-secondary">
            실제 승격·수정은 승인 후 별도 작업으로 진행하며, 끝나면 이 목록에서 해당 항목을 고치거나 지웁니다.
          </p>
        </div>
      )}
    </div>
  );
}

/** 실제 사용 화면 — 메뉴 위치 + 새 창 링크(앱 화면이라 로그인 필요) */
function UsedIn({ c }: { c: LocalComp }) {
  const list = SCREENS[`${c.agent}:${c.name}`] ?? [];
  return (
    <div className="mb-3 text-caption">
      <div className="mb-1 font-semibold text-text-secondary">사용 화면 {list.length ? '' : '— 없음'}</div>
      {list.length > 0 && (
        <ul className="flex flex-col gap-1">
          {list.map((s) => (
            <li key={s.label + s.path}>
              {s.path ? (
                <a href={s.path} target="_blank" rel="noopener noreferrer" className="text-point underline-offset-2 hover:underline">
                  {s.label} ↗
                </a>
              ) : (
                <span className="text-text-secondary">{s.label}</span>
              )}
              {s.note && <span className="ml-2 text-text-secondary">({s.note})</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CompCard({ c }: { c: LocalComp }) {
  const demo = c.noSample ? undefined : DEMOS[c.agent]?.[c.name];
  return (
    <article id={cardId(c)} className="flex min-w-0 scroll-mt-6 flex-col rounded-xxl border border-border bg-surface-raised p-4">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-heading-sm text-text">{c.name}</h3>
        <Badge status={VERDICT_STATUS[c.verdict]}>{VERDICT_LABEL[c.verdict]}</Badge>
        <span className="ml-auto text-caption text-text-secondary">
          {c.author} · {c.createdAt}
        </span>
      </header>
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-caption text-text-secondary">
        <span className="break-all font-mono">packages/agents/{c.agent}/src/{c.file}</span>
        <span>사용처 {c.uses}</span>
        <span>ui-core 중복 {c.overlap || '—'}</span>
      </div>
      <p className="text-sm text-text">{c.desc}</p>
      <p className="mb-3 mt-1 text-sm text-text-secondary">{c.reason}</p>
      <UsedIn c={c} />
      <div className="mt-auto overflow-auto rounded-lg border border-border bg-surface p-4">
        {demo ? (
          demo()
        ) : (
          <p className="text-sm text-text-secondary">라이브 샘플 없음 — {c.noSample ?? '샘플 미작성'}</p>
        )}
      </div>
    </article>
  );
}

/**
 * 에이전트 로컬 컴포넌트 갤러리 — guides 허브(/guides/agent-components, 로그인 불필요)와 앱 레이아웃 안(/agent-components, 개발자 메뉴) 두 곳에서 연다. 프로덕션 미등록.
 * 에이전트를 하나씩 골라 보고, 카드마다 실제 컴포넌트 샘플과 승격·수정·유지 판정을 함께 본다.
 */
export function AgentComponentsPage() {
  const [agent, setAgent] = useState(readHash);
  const [filter, setFilter] = useState('all');
  const [crossOpen, setCrossOpen] = useState(false);

  useEffect(() => {
    const onHash = () => setAgent(readHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const pick = (a: string) => {
    window.history.replaceState(null, '', `#${a}`);
    setAgent(a);
  };

  const agentComps = COMPS.filter((c) => c.agent === agent);
  const shown = filter === 'all' ? agentComps : agentComps.filter((c) => c.verdict === filter);

  return (
    <div className="min-h-screen bg-surface p-6 text-text md:p-10">
      <div className="mb-6">
        <div className="font-mono text-xs text-text-secondary">packages/agents/*/src/components · guides</div>
        <h1 className="mt-1 text-heading-lg">에이전트 로컬 컴포넌트</h1>
        <p className="mt-2 text-sm text-text-secondary">
          ui-core 에 없고 에이전트 안에만 있는 컴포넌트 {COMPS.length}개 (승격 필요 {count(COMPS, '승격')} · 수정 필요 {count(COMPS, '수정')} · 유지{' '}
          {count(COMPS, '유지')}). components 폴더 외에 기능 명세에 등재된 화면 내부 컴포넌트(경로가 pages/)도 포함한다. 샘플은 실제 컴포넌트에 목업 데이터를 넣어 렌더하며, 샘플의 API 요청은 차단된다(guides 화면은 전부, 앱 화면은 조회 외 쓰기 요청).
        </p>
        <button
          type="button"
          className="mt-3 text-sm font-semibold text-point"
          aria-expanded={crossOpen}
          onClick={() => setCrossOpen((v) => !v)}
        >
          에이전트 간 중복 묶음 {CROSS_ITEMS.length}건 {crossOpen ? '▴' : '▾'}
        </button>
        {crossOpen && (
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {CROSS_ITEMS.map((x) => (
              <div key={x.title} className="rounded-xxl border border-border bg-surface-raised p-4">
                <div className="text-sm font-semibold text-text">{x.title}</div>
                <div className="mt-1 text-caption text-text-secondary">{x.body}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 좁은 화면 — 사이드바 대신 상단 에이전트 선택 */}
      <div className="mb-4 overflow-x-auto lg:hidden">
        <Segmented options={AGENTS.map((a) => ({ label: a, value: a }))} value={agent} onChange={pick} />
      </div>

      <div className="flex gap-6">
        <nav className="sticky top-6 hidden w-56 shrink-0 self-start lg:block" aria-label="에이전트">
          <ul className="flex flex-col gap-1">
            {AGENTS.map((a) => {
              const n = COMPS.filter((c) => c.agent === a).length;
              return (
                <li key={a}>
                  <button
                    type="button"
                    onClick={() => pick(a)}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left font-mono text-sm ${
                      a === agent ? 'bg-surface-raised font-semibold text-point' : 'text-text-secondary'
                    }`}
                  >
                    {a}
                    <span className="text-caption">{n}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <ul className="mt-4 flex flex-col gap-1 border-t border-border pt-4">
            {shown.map((c) => (
              <li key={cardId(c)}>
                <a href={`#${agent}`} onClick={(e) => { e.preventDefault(); document.getElementById(cardId(c))?.scrollIntoView({ behavior: 'smooth' }); }} className="block truncate px-3 py-1 text-caption text-text-secondary">
                  {c.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <main className="min-w-0 flex-1">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <h2 className="font-mono text-heading-md">{agent}</h2>
            <Segmented
              size="sm"
              options={[
                { label: `전체 ${agentComps.length}`, value: 'all' },
                { label: `승격 필요 ${count(agentComps, '승격')}`, value: '승격' },
                { label: `수정 필요 ${count(agentComps, '수정')}`, value: '수정' },
                { label: `유지 ${count(agentComps, '유지')}`, value: '유지' },
              ]}
              value={filter}
              onChange={setFilter}
            />
            <VerdictHelp />
          </div>
          {/* 카드 2열(넓은 화면) — 한 화면에 더 많이 보이도록 */}
          <div className="grid gap-4 xl:grid-cols-2">
            {shown.map((c) => (
              <CompCard key={cardId(c)} c={c} />
            ))}
            {!shown.length && <p className="text-sm text-text-secondary">해당 판정의 컴포넌트가 없습니다.</p>}
          </div>
        </main>
      </div>
    </div>
  );
}
